import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const API_URL = `${API_BASE}/api/auth`;

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface SignupCredentials extends LoginCredentials {
  confirmPassword: string;
  name?: string;
}

export interface AuthResponse {
  token: string;
  user: {
    id: string | number;
    email: string;
    name?: string;
  };
}

const authService = {
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    const response = await axios.post(`${API_URL}/login`, credentials);
    return response.data;
  },

  signup: async (credentials: SignupCredentials): Promise<AuthResponse> => {
    const response = await axios.post(`${API_URL}/register`, {
      email: credentials.email,
      password: credentials.password,
      name: credentials.name || credentials.email.split('@')[0],
    });
    return response.data;
  },

  me: async (): Promise<AuthResponse['user'] & { role?: string }> => {
    const response = await axios.get(`${API_URL}/me`, {
      headers: { Authorization: `Bearer ${authService.getToken()}` },
    });
    return response.data;
  },

  forgotPassword: async (email: string): Promise<void> => {
    await axios.post(`${API_URL}/forgot-password`, { email });
  },

  resetPassword: async (token: string, password: string): Promise<void> => {
    await axios.post(`${API_URL}/reset-password`, { token, password });
  },

  /** Start Google OAuth (server redirects; JWT is never placed in the URL). */
  googleAuth: () => {
    window.location.href = `${API_URL}/google`;
  },

  /**
   * Exchange single-use OAuth code from /oauth/callback?code=... for a JWT session.
   */
  exchangeOAuthCode: async (code: string): Promise<AuthResponse> => {
    const response = await axios.post(`${API_URL}/oauth/exchange`, { code });
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
  },

  getToken: (): string | null => {
    return localStorage.getItem('token') || sessionStorage.getItem('token');
  },

  setToken: (token: string, remember: boolean = false) => {
    if (remember) {
      localStorage.setItem('token', token);
      sessionStorage.removeItem('token');
    } else {
      sessionStorage.setItem('token', token);
      localStorage.removeItem('token');
    }
  },

  setupAxiosInterceptors: () => {
    axios.interceptors.request.use(
      (config) => {
        const token = authService.getToken();
        if (token) {
          config.headers = config.headers || {};
          (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
  },
};

authService.setupAxiosInterceptors();

export default authService;
